<template>
  <a class="position-relative">
    <span style="font-size: 0"
          @click="notificationsPressed">
      <BellSvg class="pointer"/>
    </span>
    <span class="notification-bubble"
          v-if="notificationsCounter > 0">
      {{ notificationsCounter }}
    </span>
    <b-dropdown menu-class="mt-3 header-notifications"
                right
                ref="dropdown"
                variant="link"
                no-caret>
      <div>
        <div class="border-bottom"
             v-if="notificationsCounter > 0 && more">
          <a @click="readAll"
             class="text-center d-block p-2 pointer text-muted text-underlined font-weight-normal">
            {{ $t('read_all') }}
          </a>
        </div>
        <div v-for="(n, nind) in notifications"
             :key="`notif-${n.id}`"
             class="dropdown-item">
          <NotificationTeaser :notification="n"
                              @onShowTask="onShowTask"
                              @onDelete="notificationDeleted(n)"
                              :class="{'border-bottom': (nind + 1) < notifications.length }"/>
        </div>
        <div v-if="loaded && !notifications.length"
             class="dropdown-item">
          {{ $t('no_notifications') }}
        </div>
        <div v-if="more">
          <ScrollToEventElement @visible="loadMore">
            <a v-if="more"
               class="text-center p-3 d-block"
               @click="loadMore">
              <RefreshSvg class="pointer"/>
            </a>
          </ScrollToEventElement>
        </div>
      </div>
    </b-dropdown>
  </a>
</template>

<script setup>
import BellSvg from '@/assets/svg-vue/header/bell.svg'
import RefreshSvg from '@/assets/svg-vue/general/refresh2.svg'
import { EloquentService } from "@/services/api/eloquent.api.service"
import { MarketingService } from "@/services/api/marketing.api.service"
import NotificationTeaser from "@/components/parts/header/NotificationTeaser"
import ScrollToEventElement from "@/components/parts/general/ScrollToEventElement.vue"
import { copyObject } from "@/extensions/prototypes/prototypes"
import { computed, getCurrentInstance, onBeforeUnmount, ref } from "vue"
import { useStore } from "vuex"

const store = useStore()
const instance = getCurrentInstance()

const dropdown = ref(null)
const notifications = ref([])
const page = ref(1)
const loaded = ref(false)
const loading = ref(false)
const more = ref(false)
const allRead = ref(false)

// not reactive: not used in the template
let timer = null

const headerCounters = computed(() => store.state.auth.headerCounters)
const user = computed(() => store.state.auth.user)
const notificationsCounter = computed(() => headerCounters.value.unread)

const notificationsPressed = () => {
  if(dropdown.value.visible) {
    if(timer) clearTimeout(timer)
    dropdown.value.hide()
    return
  }
  if(loading.value) return // the first page is still loading: a second load would add it twice
  page.value = 1
  notifications.value = []
  load()
}

const loadMore = () => {
  if(loading.value) return
  page.value++
  load()
}

// loads a page of notifications, opens the dropdown and marks all the shown ones read after 2 seconds
const load = () => {
  loading.value = true
  EloquentService.dataTable('notification', page.value, {
    condition: {
      recipient_id: user.value.id,
      show_in_header: 1,
      sent_at: 'whereNotNull'
    },
    order: 'sent_at',
    sort: 'desc',
    pagination:  16,
  }).then(res => {
    notifications.value = notifications.value.concat(res.data.result.data)
    more.value = res.data.result.current_page < res.data.result.last_page
    loading.value = false
    loaded.value = true
    dropdown.value.show()
    if(timer) clearTimeout(timer) // the new timer marks the earlier pages too
    timer = setTimeout(() => {
      let ids = notifications.value.filter(s => !s.is_read).map(s => s.id)
      if(ids.length) {
        MarketingService.readNotifications(ids).then(res => {
          if(!res.data) return
          notifications.value.forEach(item => {
            if(ids.includes(item.id)) item.is_read = true
          })
          store.commit('incrementHeaderUpdateCounter')
        })
      }
    }, 2000)
  }).catch(e => {
    console.log(e)
    loading.value = false
  })
}

// removed by reference: the list may have been reloaded in the meantime
const notificationDeleted = (notification) => {
  setTimeout(() => {
    const index = notifications.value.indexOf(notification)
    if(index !== -1) notifications.value.splice(index, 1)
  }, 20)
}

const readAll = async () => {
  if(allRead.value) return
  try {
    allRead.value = true
    await MarketingService.readNotifications(['all'])
    store.commit('incrementHeaderUpdateCounter')
  } catch (e) {
    console.log(e)
  } finally {
    allRead.value = false
  }
}

const onShowTask = async (taskId) => {
  const task = await EloquentService.show('task', taskId)
  if(!task?.data) return
  store.commit('setTask', copyObject(task.data))
  instance?.proxy?.$bvModal.show('task-show-modal')
}

onBeforeUnmount(() => {
  clearTimeout(timer)
})
</script>

<style scoped>
.notification-bubble {
  top: 0;
  left: 12px;
}
</style>
